import { computed, reactive, ref, watch } from "@vue/composition-api";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

/**
 * Code stable de l'origine de génération, résolu côté back.
 *
 * On envoie le CODE et jamais l'id : les identifiants meta_option diffèrent
 * d'un environnement à l'autre, et un id en dur ici finirait par désigner la
 * mauvaise origine en prod. (Le motif inverse existe encore dans
 * global-watch/useGlobalWatchSubmit.js, avec `RMR_ORIGIN_PAGE_ID = 89`.)
 */
const ORIGIN_CODE = "video_transcription";

/**
 * L'instance axios de l'app est créée SANS timeout (src/plugins/axios.js), donc
 * sans cette option un appel qui ne revient jamais laisse un spinner infini —
 * on l'a déjà eu sur le scraping. Une transcription est longue par nature :
 * lecture de sous-titres quasi immédiate, mais Whisper ou Gemini doivent
 * télécharger puis transcrire l'audio.
 *
 * Si le client abandonne, le serveur, lui, va au bout et écrit le résultat dans
 * son cache : relancer l'analyse sur la même URL répond alors instantanément.
 */
const TRANSCRIPTION_TIMEOUT_MS = 180000;

/**
 * Statut envoye quand la correspondance id -> nom n'est pas encore connue.
 * Volontairement le statut NON destructif : au pire on cree un brouillon de
 * plus, jamais une publication non voulue.
 */
const FALLBACK_STATUS = "draft";

/**
 * Position INITIALE de l'interrupteur « Découper en plusieurs angles », rien
 * de plus : au-delà de 3 minutes une vidéo porte en général plusieurs sujets.
 * Le seuil qui fait autorité est celui du back (VIDEO_LONG_THRESHOLD_S), et il
 * ne sert qu'aux appels sans interface (process via sources, full auto) : dès
 * que cette page appelle, elle envoie le booléen de l'interrupteur, qui gagne.
 */
const SPLIT_SUGGESTED_ABOVE_S = 180;

/** Plafond d'angles demandés au LLM pour une vidéo découpée. */
const MAX_ANGLES = 5;

/**
 * Valeur du sélecteur qui laisse le back décider : la brique par défaut de
 * la plateforme (config admin), puis la brique de fallback en cas d'échec.
 */
export const DEFAULT_PROVIDER = "";

/**
 * Détection de plateforme côté navigateur, par hôte uniquement.
 *
 * Elle ne sert qu'à griser les briques qui ne gèrent pas l'URL saisie AVANT
 * l'analyse. La détection qui fait foi est celle du back (urls.py). Un hôte
 * inconnu est une vidéo « Autre » (voie audio yt-dlp) : cette page ne reçoit
 * que des vidéos. `null` = pas encore une URL.
 */
export const detectPlatformFromUrl = (url) => {
  let host = "";
  try {
    host = new URL((url || "").trim()).hostname.toLowerCase();
  } catch (error) {
    return null;
  }
  if (!host) return null;
  if (/(^|\.)youtube\.com$|(^|\.)youtu\.be$/.test(host)) return "youtube";
  if (/(^|\.)instagram\.com$/.test(host)) return "instagram";
  if (/(^|\.)tiktok\.com$/.test(host)) return "tiktok";
  if (/(^|\.)x\.com$|(^|\.)twitter\.com$/.test(host)) return "x";
  return "other";
};

const useVideoTranscription = () => {
  const { axiosGet, axiosPost, axiosPut } = useAxios();
  const { store, router } = useProxy();

  const state = reactive({
    url: "",
    /**
     * `key` de la brique choisie explicitement (cf. /transcription-providers),
     * ou DEFAULT_PROVIDER pour laisser le back décider. Un choix explicite est
     * STRICT côté serveur : pas de fallback sur une autre brique.
     */
    provider: DEFAULT_PROVIDER,
    /** Ignorer le cache et retranscrire — pour comparer les briques. */
    force: false,
    /** Catalogue des briques, chargé une fois. */
    providers: [],
    /**
     * `false` par defaut : l'angle saisi sert de SUJET, et le titre du post est
     * fabrique par le LLM a partir de ce sujet et du contenu. Coche, l'angle
     * devient le titre definitif sans passer par la generation de titre.
     * Cf. aif_process_next_post_generation.py, branche `if item["topic_is_title"]`.
     */
    topicIsTitle: false,
    /** null tant qu'aucune analyse n'a abouti. */
    source: null,
    /**
     * Brouillon de la transcription, éditable. Distinct de `source.content`
     * (ce que le serveur a rendu) : la différence des deux, c'est `dirty`.
     */
    transcriptDraft: "",
    savingTranscript: false,
    /**
     * Interrupteur « Découper en plusieurs angles ». Positionné à l'analyse
     * selon la durée, puis à la main. Forcé à `false` et grisé quand la vidéo
     * n'est pas horodatée : sans time codes il n'y a rien à découper (voie
     * RapidAPI Instagram).
     */
    split: false,
    /** `[{ angle, start_s, end_s, selected }]` — vide tant qu'on n'a pas proposé. */
    angles: [],
    error: null,
    analyzing: false,
    proposing: false,
    sending: false,
    /** Progression de l'envoi : l'endpoint prend UN article par appel. */
    sentCount: 0,
    totalToSend: 0,
    snackbar: { show: false, color: "success", message: "", actions: [] },
  });

  // =>  COMPUTED
  const selectedSiteId = computed(
    () => Number(store.getters["aiFactory/getSelectedSites"] || 0) || null
  );

  const selectedTemplateId = computed(
    () =>
      Number(store.getters["aiFactory/getCustomPromptTemplate"] || 0) || null
  );

  const imageHandler = computed(
    () => store.getters["aiFactory/getGenerationImageHandler"] || "generate"
  );

  /**
   * Le StatusFilter stocke un ID de meta_option (aif_publication_status), pas
   * le statut WordPress attendu par l'API — envoyer l'id tel quel repond 422.
   *
   * On resout par le NOM de l'option, qui vaut exactement « draft » ou
   * « publish », plutot que de comparer a des ids en dur comme le fait
   * UrlBatch.vue (`=== 84` / `=== 85`) : ces ids different d'un environnement
   * a l'autre.
   */
  // `ref` et non `reactive` : en Vue 2, ajouter des cles a un objet reactif
  // apres coup ne declenche rien (d'ou le `set` importe par StatusFilter).
  // Reassigner la ref d'un bloc evite le piege.
  const statusNameById = ref({});

  const publicationStatus = computed(
    () =>
      statusNameById.value[store.getters["aiFactory/getPublicationStatus"]] ||
      FALLBACK_STATUS
  );

  const urlPlatform = computed(() => detectPlatformFromUrl(state.url));

  /**
   * Les briques pour le sélecteur : « Par défaut » en tête, puis le catalogue,
   * chaque brique grisée si elle ne gère pas la plateforme de l'URL saisie.
   */
  const providerItems = computed(() => [
    {
      value: DEFAULT_PROVIDER,
      text: "Par défaut (brique de la plateforme, fallback si échec)",
      disabled: false,
    },
    ...state.providers.map((item) => ({
      value: item.key,
      text: item.label,
      disabled:
        !!urlPlatform.value && !item.platforms.includes(urlPlatform.value),
    })),
  ]);

  const selectedProvider = computed(
    () => state.providers.find((item) => item.key === state.provider) || null
  );

  /** La brique choisie ne rend pas de time codes : la découpe sera impossible. */
  const providerLacksTimestamps = computed(
    () => !!selectedProvider.value && !selectedProvider.value.has_timestamps
  );

  const canAnalyze = computed(
    () => !!state.url.trim() && !!selectedSiteId.value && !state.analyzing
  );

  const canSplit = computed(() => !!state.source?.has_timestamps);

  const transcriptDirty = computed(
    () =>
      !!state.source &&
      state.transcriptDraft.trim() !== (state.source.content || "").trim()
  );

  const canSaveTranscript = computed(
    () =>
      transcriptDirty.value &&
      !!state.transcriptDraft.trim() &&
      !state.savingTranscript &&
      !!state.source?.is_video
  );

  const canPropose = computed(
    () =>
      !!state.source &&
      !state.proposing &&
      !state.sending &&
      !transcriptDirty.value
  );

  const selectedAngles = computed(() =>
    state.angles.filter((item) => item.selected && item.angle.trim())
  );

  const canSubmit = computed(
    () => selectedAngles.value.length > 0 && !state.sending
  );

  const sendProgress = computed(() =>
    state.totalToSend ? `${state.sentCount} / ${state.totalToSend}` : ""
  );

  // =>  METHODS
  const notify = (message, color = "success") => {
    state.snackbar = { show: true, color, message, actions: [], timeout: 6000 };
  };

  const loadPublicationStatuses = async () => {
    try {
      const {
        data: { items },
      } = await axiosGet("/meta-options/aif_publication_status/");
      statusNameById.value = Object.fromEntries(
        items.map((item) => [item.id, item.name])
      );
      // Brouillon par défaut : le store arrive vide, et le select le montrait
      // vide alors que l'envoi retombait déjà sur `draft`. On aligne ce que
      // l'éditeur voit sur ce qui part — sans écraser un choix déjà fait.
      const current = store.getters["aiFactory/getPublicationStatus"];
      const draft = items.find((item) => item.name === FALLBACK_STATUS);
      if (draft && !statusNameById.value[current]) {
        store.dispatch("aiFactory/updatePublicationStatus", draft.id);
      }
    } catch (error) {
      // Sans la correspondance on retombe sur le brouillon : la page reste
      // utilisable, elle ne peut simplement pas publier directement.
      console.error(error);
    }
  };

  const loadProviders = async () => {
    try {
      const { data } = await axiosGet(
        "/api/ai-factory/transcription-providers"
      );
      state.providers = data.providers || [];
    } catch (error) {
      // Sans catalogue, le sélecteur ne propose que « Par défaut » : la page
      // reste utilisable comme avant.
      console.error(error);
    }
  };

  /**
   * Succes : on propose d'aller voir la demande dans la file, comme le font
   * deja la veille globale, le Lot d'URLs et les sources internes.
   *
   * `timeout: -1` est indispensable — le snackbar porte une action, il ne doit
   * pas disparaitre tout seul avant que l'editeur ait pu cliquer.
   */
  const notifySent = (okCount, koCount) => {
    // Un echec partiel ne doit pas passer pour un succes : sur N envois
    // sequentiels, certains peuvent echouer seuls (site inaccessible, template
    // supprime entre-temps) et l'editeur doit savoir combien relancer.
    const message = koCount
      ? `${okCount} demande(s) envoyée(s), ${koCount} en échec.`
      : `${okCount} demande(s) envoyée(s). Les articles sont en file de génération.`;

    state.snackbar = {
      show: true,
      color: koCount ? "warning" : "success",
      message,
      timeout: -1,
      actions: [
        {
          text: "OK",
          onClick: () =>
            router.push({
              name: "ai-factory-post-generation-list",
              query: selectedSiteId.value
                ? { site_id: selectedSiteId.value }
                : {},
            }),
        },
      ],
    };
  };

  const analyze = async () => {
    state.analyzing = true;
    state.source = null;
    state.transcriptDraft = "";
    state.angles = [];
    state.error = null;

    try {
      const { data } = await axiosPost(
        "/api/ai-factory/source-preview",
        {
          url: state.url.trim(),
          site_id: selectedSiteId.value,
          provider: state.provider || null,
          force: state.force,
        },
        { timeout: TRANSCRIPTION_TIMEOUT_MS }
      );

      if (data.success) {
        state.source = data.source;
        state.transcriptDraft = data.source.content || "";
        // Position initiale seulement : l'editeur voit la duree dans la carte
        // d'apercu et decide. Sans time codes, la decoupe est impossible.
        state.split =
          !!data.source.has_timestamps &&
          Number(data.source.video_duration_s || 0) > SPLIT_SUGGESTED_ABOVE_S;
        // Une retranscription forcée est un geste ponctuel, pas un réglage.
        state.force = false;
      } else {
        // Une source indisponible répond 200 avec son motif : on l'affiche tel
        // quel plutôt qu'un message générique, c'est ce qui permet à l'éditeur
        // de savoir si la vidéo est privée, bloquée ou muette.
        state.error = data.source.error || "Source indisponible";
      }
    } catch (error) {
      state.error =
        error.code === "ECONNABORTED"
          ? "La transcription dépasse le délai d'attente. Le serveur la termine de son côté : relancez l'analyse dans une minute, la réponse sera immédiate."
          : error.response?.data?.detail || "L'analyse a échoué";
    } finally {
      state.analyzing = false;
    }
  };

  /**
   * Enregistre la transcription corrigée : la ligne de cache devient la
   * référence pour la génération. Les angles déjà proposés sont invalidés,
   * ils ont été calculés sur l'ancien texte.
   */
  const saveTranscript = async () => {
    if (!canSaveTranscript.value) return;
    state.savingTranscript = true;
    state.error = null;

    try {
      const { data } = await axiosPut("/api/ai-factory/video-transcript", {
        url: state.source.url,
        text: state.transcriptDraft,
      });
      state.source = {
        ...state.source,
        content: state.transcriptDraft.trim(),
        provider: data.provider,
        has_timestamps: data.has_timestamps,
      };
      state.transcriptDraft = state.source.content;
      state.angles = [];
      state.split = state.split && !!data.has_timestamps;
      notify("Transcription enregistrée. Les angles sont à reproposer.");
    } catch (error) {
      state.error = error.response?.data?.detail || "L'enregistrement a échoué";
    } finally {
      state.savingTranscript = false;
    }
  };

  const resetTranscriptDraft = () => {
    state.transcriptDraft = state.source?.content || "";
  };

  /**
   * `more = true` : « Proposer d'autres angles » — les angles déjà affichés
   * partent en exclusion et les nouveaux s'AJOUTENT, non cochés, sans toucher
   * aux existants. Gary veut d'autres angles, pas les remplacer.
   */
  const proposeAngles = async ({ more = false } = {}) => {
    state.proposing = true;
    state.error = null;

    const excluded = more
      ? state.angles.map((item) => item.angle.trim()).filter(Boolean)
      : [];

    try {
      const { data } = await axiosPost(
        "/api/ai-factory/video-angles",
        {
          url: state.source.url,
          site_id: selectedSiteId.value,
          max_angles: MAX_ANGLES,
          split: state.split,
          exclude_angles: excluded,
        },
        { timeout: TRANSCRIPTION_TIMEOUT_MS }
      );

      if (!data.success) {
        state.error = data.error || "Proposition d'angles impossible";
        return;
      }

      const proposed = (data.angles || []).map((item) => ({
        angle: item.angle,
        start_s: item.start_s,
        end_s: item.end_s,
        // Tout coché au premier passage : le cas courant est « je prends ce
        // qu'on me propose ». Les angles supplémentaires arrivent décochés :
        // l'éditeur choisit ce qu'il ajoute.
        selected: !more,
      }));

      state.angles = more ? [...state.angles, ...proposed] : proposed;

      if (!proposed.length) {
        state.error = more
          ? "Aucun angle supplémentaire n'a pu être proposé."
          : "Aucun angle n'a pu être proposé pour cette vidéo.";
      }
    } catch (error) {
      state.error =
        error.code === "ECONNABORTED"
          ? "La proposition d'angles dépasse le délai d'attente. Relancez-la dans une minute."
          : error.response?.data?.detail || "La proposition d'angles a échoué";
    } finally {
      state.proposing = false;
    }
  };

  const proposeMoreAngles = () => proposeAngles({ more: true });

  const addAngle = () => {
    state.angles.push({
      angle: "",
      start_s: null,
      end_s: null,
      selected: true,
    });
  };

  const removeAngle = (index) => {
    state.angles.splice(index, 1);
  };

  const clearSelection = () => {
    state.angles.forEach((item) => {
      item.selected = false;
    });
  };

  /**
   * L'endpoint de mise en file prend UN article par appel : on boucle, comme le
   * fait deja `sendDraftPayloads` pour la veille globale. Une boucle sequentielle
   * plutot qu'un Promise.all : ces envois ecrivent en base et on prefere un
   * decompte lisible a un pic de N requetes simultanees.
   */
  const submit = async () => {
    const angles = selectedAngles.value;
    if (!angles.length) return;

    state.sending = true;
    state.sentCount = 0;
    state.totalToSend = angles.length;

    let okCount = 0;
    let koCount = 0;

    for (const item of angles) {
      try {
        await axiosPost("/api/ai-factory/add-to-post-generation-queue", {
          user_email: localStorage.getItem("userEmail") || null,
          site_id: selectedSiteId.value,
          topic: item.angle.trim(),
          topic_is_title: state.topicIsTitle,
          status: publicationStatus.value,
          image_handler: imageHandler.value,
          rmr_origin_page_code: ORIGIN_CODE,
          ...(selectedTemplateId.value
            ? { aif_custom_prompt_template_id: selectedTemplateId.value }
            : {}),
          // `content` volontairement absent : la génération rescrapera l'URL et
          // retrouvera la transcription dans le cache posé par l'analyse (et
          // corrigée à la main le cas échéant) — donc sans nouvel appel ni
          // coût. C'est alors le résultat du scrape qui décide de l'enrobage
          // « transcription vidéo », signal plus fiable que le `source_type`
          // déclaré ici.
          //
          // Seules les BORNES voyagent : c'est get_sources_text qui découpe le
          // transcript côté serveur. Un transcript d'une heure ne transite donc
          // jamais par le navigateur, et deux angles d'une même vidéo donnent
          // deux passages distincts sans rien recopier en base.
          sources: [
            {
              url: state.source.url,
              source_type: state.source.is_video ? "video" : "web",
              start_s: item.start_s,
              end_s: item.end_s,
            },
          ],
        });
        okCount += 1;
      } catch (error) {
        console.error(error);
        koCount += 1;
      }
      state.sentCount += 1;
    }

    state.sending = false;
    state.totalToSend = 0;

    notifySent(okCount, koCount);
    if (okCount) reset();
  };

  const reset = () => {
    state.url = "";
    state.topicIsTitle = false;
    state.source = null;
    state.transcriptDraft = "";
    state.split = false;
    state.angles = [];
    state.error = null;
  };

  // =>  WATCH
  // La brique choisie ne gère pas la plateforme de la nouvelle URL : on revient
  // au défaut plutôt que de laisser partir un appel voué au 422.
  watch(urlPlatform, (platform) => {
    if (!platform || !selectedProvider.value) return;
    if (!selectedProvider.value.platforms.includes(platform)) {
      state.provider = DEFAULT_PROVIDER;
    }
  });

  // =>  HOOKS
  loadPublicationStatuses();
  loadProviders();

  // =>  DATA
  return {
    state,
    analyze,
    saveTranscript,
    resetTranscriptDraft,
    proposeAngles,
    proposeMoreAngles,
    addAngle,
    removeAngle,
    clearSelection,
    submit,
    reset,
    notify,
    canAnalyze,
    canPropose,
    canSplit,
    canSaveTranscript,
    canSubmit,
    transcriptDirty,
    providerItems,
    selectedProvider,
    providerLacksTimestamps,
    urlPlatform,
    selectedAngles,
    selectedSiteId,
    sendProgress,
  };
};

export default useVideoTranscription;
